import { ServiceWithPricing } from '@/util/types/serverTypeCollection/services';
import React, { useMemo, useState } from 'react';
import { Chip } from '@heroui/chip';
import { Button } from '@heroui/react';
import { FaXmark } from 'react-icons/fa6';
import StandardModal from './StandardModal';

interface EditableInvoiceTableProps {
  services: ServiceWithPricing[];
  onRemove?: (params: {
    serviceId?: string;
    bundleId?: string;
    isBundle?: boolean;
    removeServiceFromBundle?: boolean;
  }) => void;
  showServiceDescription?: boolean;
  title?: string;
  nonRemovableServiceIds?: string[];
  usePrimaryFeeForBundleDiscount?: boolean;
}

type GroupedService = {
  bundleId?: string;
  bundleName?: string;
  services: ServiceWithPricing[];
};

interface BundleGroupRowProps {
  bundle: GroupedService;
  onRemove?: (params: {
    bundleId?: string;
    serviceId?: string;
    isBundle?: boolean;
    removeServiceFromBundle?: boolean;
  }) => void;
  showServiceDescription?: boolean;
  hasRemoveColumn: boolean;
  nonRemovableServiceIds?: string[];
  usePrimaryFeeForBundleDiscount?: boolean;
}

/** Base original (before any discounts), unscaled. */
function getBaseOriginalTotal(service: ServiceWithPricing): number {
  const totalDiscount =
    (service.bundleDiscountAmount ?? 0) +
    (service.codeDiscountAmount ?? 0) +
    (service.primaryDiscountAmount ?? 0);
  return (
    service.originalPreDiscountTotal ??
    (service.preTaxAmount ?? service.totalPrice ?? service.priceOverride ?? 0) + totalDiscount
  );
}

/**
 * Original total for discount strikethrough. When usePrimaryFee and non-primary in bundle with isPrimaryFee,
 * scale by isPrimaryFee/fee so the crossed-out amount shows primary-fee equivalent.
 */
function getOriginalTotalForDiscountDisplay(
  service: ServiceWithPricing,
  usePrimaryFeeForBundleDiscount: boolean
): number | undefined {
  const baseOriginal = getBaseOriginalTotal(service);
  if (baseOriginal <= 0) return undefined;

  if (!usePrimaryFeeForBundleDiscount) return baseOriginal;

  const fee = service.fee;
  const isPrimaryFee = service.isPrimaryFee;
  const inBundle = !!service.appliedBundleId;
  if (
    inBundle &&
    !service.isPrimary &&
    isPrimaryFee != null &&
    typeof fee === 'number' &&
    fee > 0
  ) {
    return Math.round(baseOriginal * (isPrimaryFee / fee) * 100) / 100;
  }
  return baseOriginal;
}

// Component for rendering bundle groups as a contained box
function BundleGroupRow({
  bundle,
  onRemove,
  showServiceDescription = false,
  hasRemoveColumn,
  nonRemovableServiceIds = [],
  usePrimaryFeeForBundleDiscount = true,
}: BundleGroupRowProps) {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [serviceToRemove, setServiceToRemove] =
    useState<ServiceWithPricing | null>(null);

  // Calculate bundle total
  const bundleTotal = useMemo(() => {
    return bundle.services.reduce(
      (sum, service) =>
        sum + (service.totalPrice || service.priceOverride || 0),
      0
    );
  }, [bundle.services]);

  // Calculate total bundle discount
  const totalBundleDiscount = useMemo(() => {
    return bundle.services.reduce(
      (sum, service) => sum + (service.bundleDiscountAmount ?? 0),
      0
    );
  }, [bundle.services]);

  const handleRemoveServiceClick = (service: ServiceWithPricing) => {
    setServiceToRemove(service);
    setConfirmModalOpen(true);
  };

  const handleConfirmRemove = () => {
    if (serviceToRemove && onRemove) {
      onRemove({
        serviceId: serviceToRemove._id,
        bundleId: bundle.bundleId,
        removeServiceFromBundle: true,
      });
    }
    setConfirmModalOpen(false);
    setServiceToRemove(null);
  };

  const renderServiceRow = (service: ServiceWithPricing) => {
    const displayPrice =
      service.priceOverride ?? service.totalPrice ?? service.preTaxAmount ?? 0;
    const hasPriceSet =
      typeof service.priceOverride === 'number' ||
      typeof service.totalPrice === 'number' ||
      typeof service.preTaxAmount === 'number';
    const hasDiscount =
      (service.bundleDiscountAmount ?? 0) > 0 ||
      (service.codeDiscountAmount ?? 0) > 0 ||
      (service.primaryDiscountAmount ?? 0) > 0;
    const originalForDisplay = getOriginalTotalForDiscountDisplay(
      service,
      usePrimaryFeeForBundleDiscount
    );

    return (
      <div
        key={service._id}
        className='rounded border border-gray-200 bg-gray-50 p-2'
      >
        <div className='flex items-start justify-between'>
          <div className='flex-1'>
            <div className='flex items-start gap-2'>
              <span className='font-medium'>{service.name}</span>
              {service.appliedDiscountCodeId && (
                <Chip size='sm' color='warning' variant='flat'>
                  {service.discountCodeName
                    ? `Code: ${service.discountCodeName}`
                    : 'Discount Code'}
                </Chip>
              )}
            </div>
            {service.description && showServiceDescription && (
              <div className='mt-1 hidden text-xs text-gray-500 sm:block'>
                {service.description}
              </div>
            )}
          </div>
          <div className='ml-4 flex items-center gap-2'>
            <div className='text-right'>
              {hasDiscount && originalForDisplay != null ? (
                <div className='flex flex-col items-end'>
                  <span className='text-sm text-gray-500 line-through'>
                    ${originalForDisplay.toFixed(2)}
                  </span>
                  <span className='font-medium text-green-600'>
                    ${displayPrice.toFixed(2)}
                  </span>
                </div>
              ) : hasPriceSet ? (
                <span className='font-medium'>${displayPrice.toFixed(2)}</span>
              ) : (
                <span className='text-sm text-gray-400'>Price TBD</span>
              )}
            </div>
            {onRemove && !nonRemovableServiceIds.includes(service._id) && (
              <Button
                isIconOnly
                size='sm'
                variant='light'
                color='danger'
                aria-label={`Remove ${service.name} from bundle`}
                onPress={() => handleRemoveServiceClick(service)}
              >
                <FaXmark size={14} className='text-gray-400' />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <tr>
        <td colSpan={hasRemoveColumn ? 3 : 2} className='px-0 py-2'>
          <div className='mx-0 rounded-lg border-2 border-warning/40 bg-warning/5'>
            {/* Bundle Header */}
            <div className='flex items-center justify-between rounded-t-lg border-b border-warning/30 bg-warning/10 px-3 py-2'>
              <div className='flex items-center gap-2'>
                <Chip
                  size='sm'
                  color='warning'
                  variant='solid'
                  className='font-semibold'
                >
                  Bundle
                </Chip>
                <span className='text-sm font-bold text-gray-800'>
                  {bundle.bundleName || 'Bundle'}
                </span>
              </div>
              {onRemove && (
                <Button
                  isIconOnly
                  size='sm'
                  variant='flat'
                  color='danger'
                  aria-label='Remove bundle'
                  onPress={() =>
                    onRemove({
                      bundleId: bundle.bundleId,
                      isBundle: true,
                    })
                  }
                >
                  <FaXmark size={16} />
                </Button>
              )}
            </div>

            {/* Bundle Services */}
            <div className='bg-white px-3 py-2'>
              <div className='space-y-2'>
                {bundle.services.map((service) => renderServiceRow(service))}
              </div>
            </div>

            {/* Bundle Total */}
            <div className='rounded-b-lg border-t border-warning/30 bg-warning/10 px-3 py-2'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-semibold text-gray-700'>
                  Bundle Total
                </span>
                <span className='font-bold text-gray-900'>
                  {bundleTotal > 0 ? (
                    `$${bundleTotal.toFixed(2)}`
                  ) : (
                    <span className='text-gray-400'>Price TBD</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Confirmation Modal for removing service from bundle */}
      <StandardModal
        show={confirmModalOpen}
        title='Remove Service from Bundle?'
        closeHandler={() => {
          setConfirmModalOpen(false);
          setServiceToRemove(null);
        }}
        acceptHandler={handleConfirmRemove}
        acceptName='Remove'
        buttonVarient='error'
        size='md'
      >
        <p>
          Are you sure you want to remove{' '}
          <strong>{serviceToRemove?.name}</strong>?
        </p>
        {totalBundleDiscount > 0 && (
          <p className='mt-2 text-danger'>
            This will remove the bundle and you will lose the bundle discount of{' '}
            <strong>${totalBundleDiscount.toFixed(2)}</strong>!
          </p>
        )}
      </StandardModal>
    </>
  );
}

export default function EditableInvoiceTable({
  services,
  onRemove,
  showServiceDescription = false,
  nonRemovableServiceIds = [],
  usePrimaryFeeForBundleDiscount = true,
}: EditableInvoiceTableProps) {
  // Group services by bundle
  const groupedServices = useMemo(() => {
    const bundles = new Map<string, GroupedService>();
    const individual: ServiceWithPricing[] = [];

    services.forEach((service) => {
      // Primary service should always be displayed individually, even if part of a bundle
      if (service.isPrimary) {
        individual.push(service);
      } else if (service.appliedBundleId) {
        const bundleId = service.appliedBundleId;
        if (!bundles.has(bundleId)) {
          bundles.set(bundleId, {
            bundleId,
            bundleName: service.bundleName,
            services: [],
          });
        }
        bundles.get(bundleId)!.services.push(service);
      } else {
        individual.push(service);
      }
    });

    return {
      bundles: Array.from(bundles.values()),
      individual,
    };
  }, [services]);

  // Totals: no discount = normal subtotal only; with bundle or discount code = before-discount subtotal + discount row + real total
  const totals = useMemo(() => {
    const hasBundle = services.some((s) => s.appliedBundleId);
    const hasDiscountCode = services.some(
      (s) => s.appliedDiscountCodeId || (s.codeDiscountAmount ?? 0) > 0
    );
    const subtotalAfterDiscounts = services.reduce(
      (sum, service) =>
        sum +
        (service.totalPrice ?? service.priceOverride ?? service.preTaxAmount ?? 0),
      0
    );
    const baseOriginalSubtotal = services.reduce(
      (sum, service) => sum + getBaseOriginalTotal(service),
      0
    );
    // When bundle present: "subtotal" = sum of primary-fee-based originals (matches per-line crossed-out)
    const primaryFeeSubtotal = services.reduce(
      (sum, service) =>
        sum +
        (getOriginalTotalForDiscountDisplay(service, usePrimaryFeeForBundleDiscount) ??
          getBaseOriginalTotal(service)),
      0
    );
    const showDiscountUI = hasBundle || hasDiscountCode;
    const displaySubtotal = showDiscountUI
      ? hasBundle
        ? primaryFeeSubtotal
        : baseOriginalSubtotal
      : subtotalAfterDiscounts;
    const savings = showDiscountUI
      ? Math.max(0, displaySubtotal - subtotalAfterDiscounts)
      : 0;
    const tax = services.reduce(
      (sum, service) => sum + (service.taxAmount || 0),
      0
    );
    const total = services.reduce(
      (sum, service) =>
        sum + (service.totalPrice || service.priceOverride || 0),
      0
    );
    return {
      showDiscountUI,
      subtotalAfterDiscounts,
      displaySubtotal,
      savings,
      tax,
      total,
    };
  }, [services, usePrimaryFeeForBundleDiscount]);

  const renderServiceRow = (service: ServiceWithPricing, isBundled = false) => {
    const displayPrice =
      service.priceOverride ?? service.totalPrice ?? service.preTaxAmount ?? 0;
    const hasPriceSet =
      typeof service.priceOverride === 'number' ||
      typeof service.totalPrice === 'number' ||
      typeof service.preTaxAmount === 'number';
    const hasDiscount =
      (service.bundleDiscountAmount ?? 0) > 0 ||
      (service.codeDiscountAmount ?? 0) > 0 ||
      (service.primaryDiscountAmount ?? 0) > 0;
    const originalForDisplay = getOriginalTotalForDiscountDisplay(
      service,
      usePrimaryFeeForBundleDiscount
    );

    return (
      <tr key={service._id} className='border-b'>
        <td className={`py-3 ${isBundled ? 'pl-8' : ''}`}>
          <div>
            <div className='items-top flex gap-2'>
              {service.name}
              {service.appliedDiscountCodeId && (
                <Chip size='sm' color='warning' variant='flat'>
                  {service.discountCodeName
                    ? `Code: ${service.discountCodeName}`
                    : 'Discount Code'}
                </Chip>
              )}
            </div>
            {service.description && showServiceDescription && (
              <div className='mt-1 hidden text-xs text-gray-500 sm:block'>
                {service.description}
              </div>
            )}
            {service.preTaxAmount !== undefined &&
              service.taxAmount !== undefined &&
              service.preTaxAmount > 0 && (
                <div className='text-sm text-gray-500'>
                  Subtotal: ${service.preTaxAmount.toFixed(2)}
                  {service.taxAmount > 0 &&
                    ` + Tax: $${service.taxAmount.toFixed(2)}`}
                </div>
              )}
          </div>
        </td>
        <td className='py-3'>
          {hasDiscount && originalForDisplay != null ? (
            <div className='flex flex-col items-end'>
              <span className='text-sm text-gray-500 line-through'>
                ${originalForDisplay.toFixed(2)}
              </span>
              <span className='font-medium text-green-600'>
                ${displayPrice.toFixed(2)}
              </span>
            </div>
          ) : hasPriceSet ? (
            `$${displayPrice.toFixed(2)}`
          ) : (
            <span className='text-gray-400'>Price TBD</span>
          )}
        </td>
        {onRemove && !isBundled && !nonRemovableServiceIds.includes(service._id) && (
          <td className='w-12 py-3 text-center'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              aria-label='Remove service'
              onPress={() => onRemove({ serviceId: service._id })}
            >
              <FaXmark size={16} className='text-gray-400' />
            </Button>
          </td>
        )}
        {onRemove && (isBundled || nonRemovableServiceIds.includes(service._id)) && <td className='w-12 py-3'></td>}
      </tr>
    );
  };

  return (
    <div className='overflow-x-auto'>
      <table className='w-full'>
        <thead>
          <tr className='border-b text-left text-sm text-gray-500'>
            <th className='py-2'>Description</th>
            <th className='text-right'>Total</th>
            {onRemove && <th className='w-12'></th>}
          </tr>
        </thead>
        <tbody>
          {/* Render individual services first */}
          {groupedServices.individual.map((service) =>
            renderServiceRow(service, false)
          )}

          {/* Render bundled services below */}
          {groupedServices.bundles.map((bundle) => (
            <BundleGroupRow
              key={bundle.bundleId}
              bundle={bundle}
              onRemove={onRemove}
              showServiceDescription={showServiceDescription}
              hasRemoveColumn={!!onRemove}
              nonRemovableServiceIds={nonRemovableServiceIds}
              usePrimaryFeeForBundleDiscount={usePrimaryFeeForBundleDiscount}
            />
          ))}

          {/* Subtotal: no discount = actual subtotal; with bundle or discount code = before-discount subtotal */}
          <tr className='border-b'>
            <td
              className={`p-3 text-right font-medium text-gray-600`}
              colSpan={onRemove ? 2 : 1}
            >
              Subtotal
            </td>
            <td className='font-medium text-gray-600'>
              ${totals.displaySubtotal.toFixed(2)}
            </td>
            {onRemove && <td></td>}
          </tr>

          {/* Discount Savings: when bundle or discount code present */}
          {totals.showDiscountUI && totals.savings > 0 && (
            <tr className='border-b'>
              <td
                className={`p-3 text-right font-medium text-green-600`}
                colSpan={onRemove ? 2 : 1}
              >
                Discount Savings
              </td>
              <td className='font-medium text-green-600'>
                -${totals.savings.toFixed(2)}
              </td>
              {onRemove && <td></td>}
            </tr>
          )}

          {/* Tax total row if any services have tax */}
          {totals.tax > 0 && (
            <tr className='border-b'>
              <td
                className={`p-3 text-right font-medium text-gray-600`}
                colSpan={onRemove ? 2 : 1}
              >
                Tax
              </td>
              <td className='font-medium text-gray-600'>
                ${totals.tax.toFixed(2)}
              </td>
              {onRemove && <td></td>}
            </tr>
          )}

          {/* Total row */}
          <tr>
            <td
              className={`p-3 text-right font-semibold`}
              colSpan={onRemove ? 2 : 1}
            >
              Total
            </td>
            <td className='font-bold'>
              {totals.total > 0 ? (
                `$${totals.total.toFixed(2)}`
              ) : (
                <span className='text-gray-400'>Price TBD</span>
              )}
            </td>
            {onRemove && <td></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
